import { getHighestProductLevelFromProductInventory, getProductsFromProductInventoryQuantityPackQuantity, getProductVersionFromProductInventory } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getSku } from 'pricing-pages-lib/utils/productsUtils';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { isMarketingContactsSku as getIsMarketingContactsSku } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { EMAIL_SENDS } from '../constants/LimitIds';
export const getContactsQuantityMultiplier = (state, {
  quantityPackSkuId
}) => {
  return getSku(state, {
    skuId: quantityPackSkuId
  }).includedWithQuantityPack;
};
export const getEmailSendsLimitMultiplier = (state, {
  quantityPackSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const quantityPack = skuMap[quantityPackSkuId];
  const {
    includedWithQuantityPack,
    packagedQuantities
  } = quantityPack;
  const emailSendLimit = packagedQuantities.find(limit => limit.name === EMAIL_SENDS);
  if (!emailSendLimit) return 0;
  return emailSendLimit.quantity / includedWithQuantityPack;
};
export const getMinimumNumberOfPurchasableContacts = ({
  includedAdditionalUnits,
  numContactsInCrm,
  marketingContactsOnRenewal,
  contactsQuantityMultiplier,
  purchasedContactsQuantity = 0,
  isMarketingContactsSku,
  totalOwnedMarketingContactsInCurrentPlan = 0
}) => {
  // missing information in state
  if (numContactsInCrm === null || isMarketingContactsSku && marketingContactsOnRenewal === null || !contactsQuantityMultiplier) {
    return null;
  }
  if (isMarketingContactsSku) {
    const billableAdditionalContacts = Math.max(0, marketingContactsOnRenewal - includedAdditionalUnits);
    let additionalContactsForSelectedPlanToMatchCurrentPlan = 0;
    while (includedAdditionalUnits + additionalContactsForSelectedPlanToMatchCurrentPlan < totalOwnedMarketingContactsInCurrentPlan) {
      additionalContactsForSelectedPlanToMatchCurrentPlan += contactsQuantityMultiplier;
    }
    return Math.ceil(Math.max(additionalContactsForSelectedPlanToMatchCurrentPlan, billableAdditionalContacts) / contactsQuantityMultiplier) * contactsQuantityMultiplier;
  } else {
    // hasn't bought contacts before
    if (!purchasedContactsQuantity) {
      // contacts in crm is fewer than minimum contacts purchase quantity
      if (numContactsInCrm < includedAdditionalUnits) {
        return includedAdditionalUnits;
      }
      // contacts in crm is fewer than quantity multiplier
      if (numContactsInCrm <= contactsQuantityMultiplier) {
        return contactsQuantityMultiplier;
      }
    }
    const purchasableContactsFromCrm = Math.ceil(numContactsInCrm / contactsQuantityMultiplier) * contactsQuantityMultiplier;
    return Math.max(purchasableContactsFromCrm, purchasedContactsQuantity);
  }
};
const _getPurchasedContactsQuantity = (state, {
  filter
}) => {
  const productLevel = getHighestProductLevelFromProductInventory(state, {
    productLine: MARKETING
  });
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: {
      productLevel,
      productLine: MARKETING
    }
  });
  if (!productVersion) return 0;
  if (filter && !filter(productVersion)) return 0;
  return getProductsFromProductInventoryQuantityPackQuantity(state, {
    productIdentifier: {
      productLevel,
      productLine: MARKETING
    },
    quantityPackApiName: 'additional-marketing-contacts'
  });
};
export const getPurchasedMarketingContactsQuantity = state => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return _getPurchasedContactsQuantity(state, {
    filter: ({
      skuId
    }) => getIsMarketingContactsSku({
      skuMap,
      skuId
    })
  });
};
export const getPurchasedNonMarketingContactsQuantity = state => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return _getPurchasedContactsQuantity(state, {
    filter: ({
      skuId
    }) => !getIsMarketingContactsSku({
      skuMap,
      skuId
    })
  });
};
export const getMaxAdditionalUnits = quantityPack => quantityPack.includedWithQuantityPack * quantityPack.maxTouchlessPurchasableQuantity;

/*
  Total units = (tier included units) + (additional units)
  We want total units to stay the same, so we need to account for
  the difference between included units when switching plans
 */
export const calculateAdditionalUnitsForTierSwitch = (oldUnits, oldQuantityPack, newQuantityPack) => {
  if (oldQuantityPack.skuId === newQuantityPack.skuId) return oldUnits;
  const packagedQuantitiesDifference = oldQuantityPack.includedWithProductVersion - newQuantityPack.includedWithProductVersion;
  const newMarketingUnits = oldUnits + packagedQuantitiesDifference;
  const newMaxAdditionalUnits = getMaxAdditionalUnits(newQuantityPack);
  const maxMarketingUnits = newMaxAdditionalUnits - newQuantityPack.includedWithProductVersion;
  return Math.max(0, Math.min(maxMarketingUnits, newMarketingUnits));
};
export const createSteppingFunction = (min, max, step) => (input, roundUp = false) => {
  return Math.min(Math[roundUp ? 'ceil' : 'round']((Math.max(input, min) - min) / step) * step + min, max - (max - min) % step);
};
export const getQuantityPackSteppingFunction = quantityPack => {
  const maxAdditionalUnits = getMaxAdditionalUnits(quantityPack);
  const {
    includedWithQuantityPack
  } = quantityPack;
  return createSteppingFunction(0, maxAdditionalUnits - maxAdditionalUnits % includedWithQuantityPack, includedWithQuantityPack);
};